import React, { useState } from 'react';
import classnames from 'classnames/bind';
import { Button } from '../../components/Button';
import { SmallNotice } from '../../components/SmallNotice';
import { InfoModal } from '../../components/InfoModal';
import { useDocumentTitle } from '../../shared/use-document-title';
import { useAuthentication } from '../../shared/use-authentication';
import { deleteAccount } from '../../../lib/use-cases/delete-account';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import styles from './AccountDeletePage.css';

const PRE_DELETE_ACCOUNT_MODAL = 'preDeleteAccountModal';
const ACCOUNT_DELETED_MODAL = 'accountDeletedModal';
const ACCOUNT_DELETE_ERROR_MODAL = 'accountDeleteErrorModal';

const cx = classnames.bind(styles);

function AccountDeletePage() {
    const { signOut } = useAuthentication();
    const [modal, setModal] = useState({ type: '' });
    const [isPending, setIsPending] = useState(false);

    useDocumentTitle('Delete account');

    const openModalForDeleteAccount = () => {
        logEvent(EVENT_TYPES.DELETE_ACCOUNT_BUTTON_CLICKED);
        setModal({ type: PRE_DELETE_ACCOUNT_MODAL });
    };

    const closeModal = () => {
        setModal({ type: '' });
    };

    const handleDeleteAccount = async () => {
        setIsPending(true);

        try {
            await deleteAccount();

            setModal({ type: ACCOUNT_DELETED_MODAL });
        } catch (e) {
            console.error(e);

            setModal({ type: ACCOUNT_DELETE_ERROR_MODAL });
        } finally {
            setIsPending(false);
        }
    };

    const finish = () => {
        signOut();
        closeModal();
    };

    return (
        <>
            <div className={styles.root}>
                <h1 className={styles.title}>Delete your account?</h1>
                <p className={styles.description}>This can’t be undone. <br /> <span>If you delete now, you’ll lose access to:</span></p>
                <ul className={styles.list}>
                    <li>Your saved life chapters and memories</li>
                    <li>Your memory chat history</li>
                    <li>Your progress building your keepsake book</li>
                </ul>
                <SmallNotice
                    iconName='alertTriangle'
                    description={(
                        <>
                            Important: Your life chapters and memory progress will be permanently lost.<br /><br />
                            <strong>Your active plan will be canceled.</strong>
                        </>
                    )}
                    withBackground
                />
                <Button
                    onClick={openModalForDeleteAccount}
                    label='Delete my account'
                    type='secondary'
                    className={styles.buttonDelete}
                    iconLeft={{
                        name: 'trash'
                    }}
                />
            </div>
            <InfoModal
                isOpen={modal.type === PRE_DELETE_ACCOUNT_MODAL}
                onCloseModal={closeModal}
                className={cx('modal', 'preDeleteAccountModal')}
                title='Delete your account'
                renderButtons={() => (
                    <>
                        <Button
                            onClick={handleDeleteAccount}
                            label='Delete Account'
                            className={styles.modalButtonDelete}
                            size='medium'
                            isLoading={isPending}
                        />
                        <Button
                            onClick={closeModal}
                            label='Back'
                            type='secondary'
                            size='medium'
                        />
                    </>
                )}
            >
                <p className={styles.modalDescription} style={{ marginBottom: 24 }}>
                    This is permanent. Your data will be deleted and you’ll lose access to your account and services.
                </p>
                <SmallNotice
                    iconName='alertTriangle'
                    description='You can’t undo this action'
                    withBackground
                />
            </InfoModal>
            <InfoModal
                isOpen={modal.type === ACCOUNT_DELETED_MODAL}
                onCloseModal={finish}
                title='Your Account Has Been Deleted'
                className={cx('modal', 'accountDeletedModal')}
            >
                <SmallNotice
                    iconName='checkNotice'
                    description='Thank you for being with us!'
                    className={styles.smallNotice}
                    withBackground
                />
            </InfoModal>
            <InfoModal
                isOpen={modal.type === ACCOUNT_DELETE_ERROR_MODAL}
                onCloseModal={closeModal}
                title='Something Went Wrong'
                className={cx('modal', 'accountDeleteErrorModal')}
            >
                <SmallNotice
                    iconName='alertTriangle'
                    description='We couldn’t delete your account right now — please try again later.'
                    withBackground
                />
            </InfoModal>
        </>
    );
}

export {
    AccountDeletePage
};
